// AdminBurnPage — /admin/burn：工时燃耗对账（第九十九批，SB-104）。
//
// 消费场景与口径见 adminBurn.mjs 头注。这一页只渲染：判断全在纯函数层。
// 仅管理员（需求方第九十九批裁）；路由挂在 adminOnly 守卫下，后端 require_admin
// 再把一次门——页面守卫是体验，服务端守卫才是边界。
import {
  Alert, Box, Chip, CircularProgress, Stack, TableBody, TableCell, TableHead,
  TableRow, Typography,
} from '@mui/material';
import api from '../api/client.mjs';
import { errKey } from '../api/errText.mjs';
import { useLoad } from '../hooks/useLoad.mjs';
import { useT } from '../i18n/index.mjs';
import AdminSubnav from '../components/AdminSubnav.jsx';
import DataTable from '../components/DataTable.jsx';
import HelpLink from '../components/HelpLink.jsx';
import { burnRange, displayRows, fmtDayShort, fmtHours, ymdOf } from './adminBurn.mjs';

// 周额占比 → 颜色：<80% 默认，80~100% 警示，超线报错色。
// 阈值对齐 OQ-D9 预算告警的 80% 惯例——同一个系统里「快到线了」应该长一个样。
function capColor(minutes, capMinutes) {
  if (capMinutes == null || capMinutes <= 0) return 'default';
  const r = minutes / capMinutes;
  if (r > 1) return 'error';
  if (r >= 0.8) return 'warning';
  return 'default';
}

function WeekCell({ minutes, capMinutes }) {
  return (
    <Stack direction="row" spacing={0.75} useFlexGap
           sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>{fmtHours(minutes)}h</Typography>
      {capMinutes != null ? (
        <Chip size="small" variant="outlined" color={capColor(minutes, capMinutes)}
              label={`/ ${fmtHours(capMinutes)}h`} />
      ) : null}
    </Stack>
  );
}

export default function AdminBurnPage() {
  const t = useT();
  const today = ymdOf(new Date());
  const range = burnRange(today);

  const load = useLoad(async () => {
    const r = await api.adminWorkLogBurn({ fromDay: range.fromDay, toDay: range.toDay });
    return r;
  }, [range.fromDay, range.toDay]);

  const rows = displayRows(load.data?.accounts, today);

  return (
    <Box>
      <AdminSubnav />
      <Typography sx={{ fontWeight: 700 }} variant="h5" component="h1">
        {t('adminBurn.title')}
        <HelpLink section="admin" />
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('adminBurn.subtitle', {
          from: fmtDayShort(range.fromDay), to: fmtDayShort(range.toDay),
        })}
      </Typography>

      {load.status === 'loading' ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : load.status === 'error' ? (
        <Alert severity="error">{t(errKey(load.error, { fallback: 'adminBurn.error' }))}</Alert>
      ) : rows.length === 0 ? (
        // 空页要说得出自己为什么空：区间里一条工时都没有 ≠ 页面坏了。
        <Alert severity="info">{t('adminBurn.empty')}</Alert>
      ) : (
        <DataTable minWidth={680}>
          <TableHead>
            <TableRow>
              <TableCell>{t('adminBurn.member')}</TableCell>
              <TableCell>{t('adminBurn.thisWeek')}</TableCell>
              <TableCell>{t('adminBurn.lastWeek')}</TableCell>
              <TableCell>{t('adminBurn.days')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.accountId}>
                <TableCell>
                  <Typography variant="body2">{r.displayName || r.email}</Typography>
                  {r.displayName ? (
                    <Typography variant="caption" color="text.secondary">{r.email}</Typography>
                  ) : null}
                </TableCell>
                <TableCell><WeekCell minutes={r.curMinutes} capMinutes={r.curCapMinutes} /></TableCell>
                <TableCell><WeekCell minutes={r.prevMinutes} capMinutes={r.prevCapMinutes} /></TableCell>
                <TableCell>
                  {/* 日粒度全列出（区间才 14 天）：9~10h/日的异常节奏要一眼可见，
                      只给「最重一天」会把连续三天 9h 读成正常。 */}
                  <Stack direction="row" spacing={0.5} useFlexGap
                         sx={{ flexWrap: 'wrap', alignItems: 'center' }}>
                    {r.days.map((d) => (
                      <Chip key={d.day} size="small" variant="outlined"
                            color={d.netMinutes >= 9 * 60 ? 'warning' : 'default'}
                            label={`${fmtDayShort(d.day)}·${fmtHours(d.netMinutes)}h`} />
                    ))}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </DataTable>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
        {t('adminBurn.capNote')}
      </Typography>
    </Box>
  );
}
